import React from 'react';
import { useFormulaCurve } from '../../hooks/useFormulaCurve';
import { CurveMiniChart } from '../HistoricalPrices/components/CurveMiniChart';
import { PricePoint } from '../HistoricalPrices/components/HistoricalUtils';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { useBaseCurrency } from '../../hooks/useBaseCurrency';
import { formatToShortString } from '../../utils/FormatValue';
import { Material } from '../../sp-types/materialType';
import { useCurrencyRates, getCurrencySymbol as getCurrencySymbolUtil } from '../../hooks/useCurrencyRates';
import { useFx } from '../../hooks/useFx';
import { useAccountStore } from '../../components/Stores/accountStore';

interface Props {
  formulaCode: string | null;
  formulaName?: string;
  material?: Material;
  mode?: 'full' | 'valueOnly' | 'curveOnly';
}

export const PriceCurveCard: React.FC<Props> = ({ formulaCode, formulaName, material, mode = 'full' }) => {
  const { data: curveHistory, isLoading, error } = useFormulaCurve(formulaCode);
  const { materialsMap } = useMaterialsStore();
  const { formulaColorDictionary } = useAccountStore();
  const baseCurrency = useBaseCurrency();
  const formulaCurrency = material?.currency || 'USD';
  
  // Use FX service with base currency (like Dashboard does)
  const { convertToBase, getRateToBase } = useFx(baseCurrency);

  console.log('[PriceCurveCard] Debug:', {
    formulaCode,
    formulaName,
    isLoading,
    error: error?.toString(),
    curveHistoryLength: curveHistory?.length,
    curveHistory: curveHistory?.slice(0, 2), // Show first 2 entries
  });

  // Get the latest curve (most recent date)
  const latestCurve = curveHistory && curveHistory.length > 0 ? curveHistory[curveHistory.length - 1] : null;
  
  console.log('[PriceCurveCard] Latest curve:', latestCurve);

  // Convert curve data to PricePoint format for CurveMiniChart
  const routePrices: PricePoint[] = React.useMemo(() => {
    if (!latestCurve) return [];

    // Use formula color from formulaColorDictionary, otherwise default purple
    const formulaId = material?.id || formulaCode;
    const color = formulaId && formulaColorDictionary[formulaId] ? formulaColorDictionary[formulaId] : '#8B5CF6';

    const months = ['M0', 'M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M10', 'M11', 'M12'];
    const prices = [
      latestCurve.price,
      latestCurve.month01,
      latestCurve.month02,
      latestCurve.month03,
      latestCurve.month04,
      latestCurve.month05,
      latestCurve.month06,
      latestCurve.month07,
      latestCurve.month08,
      latestCurve.month09,
      latestCurve.month10,
      latestCurve.month11,
      latestCurve.month12,
    ];

    const result = months
      .map((month, index) => ({
        key: month,
        name: month,
        price: prices[index] || undefined,
        color,
        id: `${formulaCode}-${month}`,
        visible: true,
      }))
      .filter(point => point.price !== undefined && point.price !== null);
    
    console.log('[PriceCurveCard] Route prices:', result);
    return result;
  }, [latestCurve, formulaCode, materialsMap, material, formulaColorDictionary]);

  const spotPrice = latestCurve?.price || 0;
  const asOfDate = latestCurve?.date ? new Date(latestCurve.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  
  // Get currency symbols
  const spotCurrencySymbol = getCurrencySymbolUtil(formulaCurrency);
  const baseCurrencySymbol = getCurrencySymbolUtil(baseCurrency);
  
  // Calculate conversion to base currency if different
  const needsConversion = formulaCurrency !== baseCurrency;
  
  // Use convertToBase to convert from formula currency to base currency
  // This follows the same pattern as Dashboard.tsx
  const { amountBase: convertedPrice, rate } = convertToBase(spotPrice, formulaCurrency);
  
  // Only show conversion if rate is available and currencies differ
  const showConversion = needsConversion && rate !== null;
  
  console.log('[PriceCurveCard] FX conversion:', {
    formulaCurrency,
    baseCurrency,
    spotPrice,
    rate,
    convertedPrice,
    needsConversion,
    showConversion
  });
  
  // Get UOM from material, default to 'mt'
  const uom = material?.unit || 'mt';
  
  // Format price with commas
  const formatPriceWithCommas = (price: number) => {
    return price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  if (!formulaCode) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-400 text-xs">Select a formula</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-400 text-xs">Loading...</p>
      </div>
    );
  }

  if (error || !latestCurve || routePrices.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-400 text-xs">No data</p>
      </div>
    );
  }

  if (mode === 'valueOnly') {
    return (
      <div className="h-full flex flex-col gap-1">
        <h4 className="text-lg whitespace-nowrap">Price per {uom}</h4>
        <div className="flex flex-col gap-0.5">
          <div className="text-lg font-semibold">
            {spotCurrencySymbol}{formatPriceWithCommas(spotPrice)}
          </div>
          {showConversion && (
            <div className="text-xs text-gray-500">
              {baseCurrencySymbol}{formatPriceWithCommas(convertedPrice)}
            </div>
          )}
        </div>
        {asOfDate && (
          <div className="text-[10px] text-gray-400">
            as of {asOfDate}
          </div>
        )}
      </div>
    );
  }

  if (mode === 'curveOnly') {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 min-h-0 max-h-24 -mx-2">
          <CurveMiniChart
            routePrices={routePrices}
            cssClass="w-full h-full"
            hideLegend={true}
            currency={spotCurrencySymbol}
            showTicks={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <h4 className="text-lg whitespace-nowrap">Price per {uom}</h4>
        <span className="text-xs text-gray-500">Trend</span>
      </div>
      
      <div className="flex flex-col gap-0.5">
        <div className="text-lg font-semibold">
          {spotCurrencySymbol}{formatPriceWithCommas(spotPrice)}
        </div>
        {showConversion && (
          <div className="text-xs text-gray-500">
            {baseCurrencySymbol}{formatPriceWithCommas(convertedPrice)}
          </div>
        )}
        <h6 className="text-xs text-gray-450">{formulaName || formulaCode}</h6>
      </div>

      <div className="flex-1 min-h-0 -mx-2">
        <CurveMiniChart
          routePrices={routePrices}
          cssClass="w-full h-full"
          hideLegend={true}
          currency={spotCurrencySymbol}
          showTicks={false}
        />
      </div>

      {asOfDate && (
        <div className="text-[10px] text-gray-400 text-right">
          as of {asOfDate}
        </div>
      )}
    </div>
  );
};
